/**
 * A published draft's campaign as Meta has it now, and the changes Wend has
 * sent it — slice 43, drawn.
 *
 * ## META'S NUMBERS, NOT WEND'S — AND ONLY THEIRS ON THE FIRST SCREEN
 *
 * Everything here comes from `MetaAdDeliveryState`, the read the API makes on
 * request. On a published draft's page it leads, and the composed draft folds
 * below it: one budget and one status on the screen, both Meta's, because two
 * figures with one label — one of them Wend's record — is a page that can say
 * ₹3,000 and ₹30,000 as "Budget per day" and not say which is real.
 *
 * ## FIGURES, CHIPS AND BADGES — THE ONE SENTENCE IS A FAILURE
 *
 * The rule slice 41's read set: a failed read prints what stopped it, because
 * a refusal that says nothing is a dead end. Everything else is a labelled
 * fact or a badge. What refuses a start is a badge per reason, in
 * `META_START_BLOCKERS`' words — the same function the API refuses with.
 *
 * ## WHERE THE MONEY IS, AND WHO SWITCHED IT ON — SLICE 52
 *
 * Two facts this panel used to get wrong, both because it read Wend's record
 * where it should have read Meta's answer.
 *
 * The **budget** was the ad set's or nothing. Somebody moving it to the
 * campaign in Ads Manager — which Meta calls campaign budget optimisation —
 * left `adSet.budget` null, drew a neutral "Not on the ad set" and refused
 * Start. Both levels are read now; `metaDeliveryBudget` is the one place the
 * screen and the API ask which is in force, and the figure's own label says
 * which level it is on. The only case Wend will not choose between is a budget
 * reported at both, drawn as two figures under two labels.
 *
 * **Whether it has been switched on before** was `record.startedAt !== null` —
 * Wend's record of its own first start. For a campaign somebody has been
 * running in Ads Manager that is null, so no blocker fired and Wend's "first"
 * start would have switched ON every ad, including one switched off there for
 * a reason. The caller passes `metaSwitchedOnBefore` now, which is the record
 * OR what Meta says, and `switched_off` reaches a campaign Wend never started.
 *
 * ## ONLY A LIVE DELIVERY EARNS GREEN
 *
 * Slice 41's rule, applied honestly: "Active" is green only while nothing on
 * the same row says it cannot deliver. A campaign Meta still calls active
 * after its end, or in an account that cannot spend, draws neutral beside the
 * red badge that says why.
 *
 * ## TIMES ARE THE AD ACCOUNT'S, AND THE READ SAYS WHEN IT WAS MADE
 *
 * Meta's `end_time` is an instant; the draft's end was a day in the ad
 * account's zone, so the instant is drawn in that zone — drawn in the
 * browser's, the last instant of 31 October in Kolkata is 1 November in Tokyo.
 * The read carries its own time and zone as a tag, so a tab left open does not
 * pass off the morning's "Active" as now.
 *
 * ## WHY IT IS ITS OWN FILE
 *
 * It imports only contracts, the design system and the formatters, so
 * `verify-meta-ads-controls-screen` renders it without the API client.
 */
import { Badge, ProportionBar, StateChip } from '@wend/ui';
import { Pause, Play } from 'lucide-react';
import type { ReactNode } from 'react';

/* `@wend/shared` directly, never through the API client. */
import {
  META_BUDGET_LABELS,
  META_START_BLOCKERS,
  metaBudgetOnTwoLevels,
  metaDeliveryBudget,
  startBlockers,
  type MetaAdControlHistory,
  type MetaAdDelivery,
  type MetaAdDeliveryState,
  type MetaDeliveryStatus,
  type MetaStartBlocker,
} from '@wend/shared';

import { formatCount, formatDateTime } from '../../lib/format';
import { DELIVERY_STATUS_LABEL, DELIVERY_STATUS_TONE, formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { ACTION_LABEL, STOP_LABEL, stoppedTone } from './meta-ad-controls';
import { Fact, Mono } from './MetaAdDraftSummary';

/** Waiting on Meta resolves itself; the rest need somebody to act. */
const BLOCKER_TONE: Readonly<Record<MetaStartBlocker, 'warn' | 'bad'>> = {
  account_cannot_spend: 'bad',
  account_unread: 'warn',
  ended: 'bad',
  video_processing: 'warn',
  video_failed: 'bad',
  video_unread: 'warn',
  campaign_gone: 'bad',
  budget_unread: 'bad',
  budget_two_levels: 'bad',
  other_ad_sets: 'bad',
  creative_changed: 'bad',
  switched_off: 'bad',
};

/** The blockers that mean a campaign Meta calls active is not delivering. */
const CANNOT_DELIVER: readonly MetaStartBlocker[] = ['ended', 'account_cannot_spend', 'account_unread', 'campaign_gone'];

/**
 * The budget's label — which level it is on, then by kind.
 *
 * Slice 52, gap 2: the label is the whole explanation of where a campaign's
 * money is set. A budget somebody moved to the campaign in Ads Manager is
 * drawn in the budget's own place and at its own size — it is the same fact,
 * and shrinking it would say it matters less — with "Campaign" in front of the
 * label saying where it lives. No sentence, no chip beside it.
 *
 * The words are `META_BUDGET_LABELS` in `@wend/shared`, not written here: the
 * figures, the start confirmation and the budget form all ask this one
 * function, and the API's own refusals name the same levels, so the four
 * cannot word it four ways. `null` is a budget neither level reported, drawn
 * as a chip rather than a figure — see {@link MetaAdDeliveryFigures}.
 */
export function budgetLabel(level: 'ad_set' | 'campaign' | null, kind: 'daily' | 'lifetime' | null): string {
  if (level === null || kind === null) return 'Budget';
  return META_BUDGET_LABELS[level][kind];
}

/** "in review" → "In review": the read's labels are written to follow a count. */
function capitalised(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * An instant in the ad account's zone, or in the browser's when the zone is
 * unknown or is not one this browser can use.
 */
export function formatInstantIn(iso: string, timezone: string | null): string {
  if (timezone !== null) {
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: timezone }).format(
        new Date(iso),
      );
    } catch {
      /* An unknown zone: the browser's, below. */
    }
  }
  return formatDateTime(iso);
}

/** The time of day alone, in the ad account's zone — for the read's tag. */
function formatTimeIn(iso: string, timezone: string | null): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeStyle: 'short',
      ...(timezone === null ? {} : { timeZone: timezone }),
    }).format(new Date(iso));
  } catch {
    return formatDateTime(iso);
  }
}

/**
 * Every state the read can be in except `ok`, drawn; `null` for `ok`, which
 * the caller draws as {@link MetaAdDeliveryStatus} and figures.
 */
export function MetaAdDeliveryRefusal({ state }: { state: Exclude<MetaAdDeliveryState, { state: 'ok' }> }) {
  switch (state.state) {
    /* The page draws no delivery for a draft that is not published. */
    case 'not_published':
      return null;

    case 'not_connected':
      return (
        <div data-role="meta-ad-delivery" data-state="not_connected">
          <StateChip state="not_set_up" label="None connected" />
        </div>
      );

    case 'not_checked':
      return (
        <div data-role="meta-ad-delivery" data-state="not_checked">
          <StateChip state="not_measured" label="Not checked" />
        </div>
      );

    case 'not_permitted':
      return (
        <div data-role="meta-ad-delivery" data-state="not_permitted">
          <Badge tone="bad">No ads permission</Badge>
        </div>
      );

    case 'elsewhere':
      return (
        <dl data-role="meta-ad-delivery" data-state="elsewhere">
          <Fact label="Ad account">
            <span className="inline-flex flex-wrap items-center gap-2">
              <Mono>{state.adAccountId}</Mono>
              <Badge tone="bad">Not connected</Badge>
            </span>
          </Fact>
        </dl>
      );

    case 'failed':
      return (
        <p
          data-role="meta-ad-delivery"
          data-state="failed"
          className="max-w-prose text-[0.88rem] leading-relaxed text-bad"
        >
          {state.failure.message}
        </p>
      );

    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/** Every state the read can be in, drawn whole — the status row and the figures for `ok`. */
export function MetaAdDeliveryView({
  state,
  timezone,
  switchedOnBefore,
}: {
  state: MetaAdDeliveryState;
  timezone: string | null;
  /** {@link metaSwitchedOnBefore}: Wend's own first start, OR Meta saying it has delivered. */
  switchedOnBefore: boolean;
}) {
  if (state.state !== 'ok') return <MetaAdDeliveryRefusal state={state} />;
  return (
    <section data-role="meta-ad-delivery" data-state="ok" className="flex flex-col gap-5">
      <MetaAdDeliveryStatus delivery={state.delivery} timezone={timezone} switchedOnBefore={switchedOnBefore} />
      <MetaAdDeliveryFigures delivery={state.delivery} timezone={timezone} />
    </section>
  );
}

/**
 * The page's status on a published draft: Meta's own word for the campaign,
 * the stub's chip, every reason a start is refused, and when this was read.
 */
export function MetaAdDeliveryStatus({
  delivery,
  timezone,
  switchedOnBefore,
}: {
  delivery: MetaAdDelivery;
  timezone: string | null;
  switchedOnBefore: boolean;
}) {
  const { campaign } = delivery;
  const blockers = startBlockers(delivery, new Date(), switchedOnBefore);
  const tone =
    campaign.status === 'active' && blockers.some((one) => CANNOT_DELIVER.includes(one))
      ? 'neutral'
      : DELIVERY_STATUS_TONE[campaign.status];

  return (
    <div className="flex flex-wrap items-center gap-2" data-role="meta-ad-delivery-status-row">
      <Badge tone={tone} data-role="meta-ad-delivery-status" data-status={campaign.status}>
        {campaign.running ? (
          <Play aria-hidden="true" className="mr-1 size-3" />
        ) : (
          <Pause aria-hidden="true" className="mr-1 size-3" />
        )}
        {capitalised(DELIVERY_STATUS_LABEL[campaign.status])}
      </Badge>
      {delivery.transport === 'stub' && <StateChip state="estimated" label="Simulated" />}
      {blockers.map((one) => (
        <Badge key={one} tone={BLOCKER_TONE[one]} data-role="meta-ad-start-blocker" data-blocker={one}>
          {META_START_BLOCKERS[one].label}
        </Badge>
      ))}
      <span
        data-role="meta-ad-delivery-read"
        className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted"
      >
        Read {formatTimeIn(delivery.readAt, timezone)}
        {timezone === null ? '' : ` · ${timezone}`}
      </span>
    </div>
  );
}

/** What the campaign can spend and has spent, as Meta has it. */
export function MetaAdDeliveryFigures({ delivery, timezone }: { delivery: MetaAdDelivery; timezone: string | null }) {
  const money = (minor: number): ReactNode =>
    formatMetaMoney(minor, delivery.currency) ?? <StateChip state="not_measured" label={delivery.currency} />;
  const { campaign, adSet } = delivery;
  const inForce = metaDeliveryBudget(delivery);
  const twoLevels = metaBudgetOnTwoLevels(delivery);

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {/*
          WHERE THE MONEY IS, SAID BY THE LABEL AND NOWHERE ELSE.

          Until slice 52 Wend read only the ad set's budget, so a budget moved
          to the campaign in Ads Manager arrived as `null` and drew the neutral
          "Not on the ad set" — a badge that reported Wend's blind spot and
          blocked Start. The campaign's budget is read now, so that badge
          describes no state that exists and is gone.

          One figure, at its own size, under a label that says which level it
          is on. Two budgets under one label is what slice 43 deleted; the one
          case where Meta reports two is drawn as two figures under two labels,
          with `budget_two_levels` beside the status saying a start cannot
          choose between them.
        */}
        {twoLevels ? (
          <>
            {/* `null` here is unreachable — `twoLevels` is both levels
                reporting — but it is written as the chip anyway, because a
                fallback of zero would print ₹0.00 for a budget nobody
                measured, and a figure that says the campaign spends nothing is
                the one mistake every other screen in this product is built to
                avoid. The branch below, and this panel's siblings, print the
                chip; so does this one. */}
            <Fact label={budgetLabel('ad_set', adSet.budget?.kind ?? null)}>
              {adSet.budget === null ? (
                <StateChip state="not_measured" label="Not read" />
              ) : (
                <span
                  data-role="meta-ad-delivery-budget"
                  data-level="ad_set"
                  className="font-mono text-[1.25rem] leading-none tabular-nums"
                >
                  {money(adSet.budget.amount)}
                </span>
              )}
            </Fact>
            <Fact label={budgetLabel('campaign', campaign.budget?.kind ?? null)}>
              {campaign.budget === null ? (
                <StateChip state="not_measured" label="Not read" />
              ) : (
                <span
                  data-role="meta-ad-delivery-budget"
                  data-level="campaign"
                  className="font-mono text-[1.25rem] leading-none tabular-nums"
                >
                  {money(campaign.budget.amount)}
                </span>
              )}
            </Fact>
          </>
        ) : (
          <Fact label={budgetLabel(inForce?.level ?? null, inForce?.budget.kind ?? null)}>
            {inForce === null ? (
              /* Neither level reported one Wend can read. Not a zero, and not
                 a claim about which level it is missing from: `budget_unread`
                 beside the status says the rest. */
              <StateChip state="not_measured" label="Not read" />
            ) : (
              <span
                data-role="meta-ad-delivery-budget"
                data-level={inForce.level}
                className="font-mono text-[1.25rem] leading-none tabular-nums"
              >
                {money(inForce.budget.amount)}
              </span>
            )}
          </Fact>
        )}
        <Fact label="Ends">
          {adSet.endTime === null ? <Badge tone="neutral">No end date</Badge> : <Mono>{formatInstantIn(adSet.endTime, timezone)}</Mono>}
        </Fact>
        <Fact label="Spent">
          {/* At the budget's size (slice 44): what it has cost is the figure a
              skimmer must not read as the smaller one beside what it may cost. */}
          {delivery.spent === null ? (
            <StateChip state="not_measured" label="Not measured" />
          ) : (
            <span data-role="meta-ad-delivery-spent" className="font-mono text-[1.25rem] leading-none tabular-nums">
              {money(delivery.spent)}
            </span>
          )}
        </Fact>
        <Fact label="Spend cap">
          {campaign.spendCap === null ? (
            /* No cap is a fact, not a limit of zero. */
            <Badge tone="neutral">No spend cap</Badge>
          ) : (
            <span className="flex flex-col gap-2">
              <Mono>{money(campaign.spendCap)}</Mono>
              {delivery.spent !== null && (
                /* `bad` only at the cap, where Meta stops delivery. */
                <ProportionBar
                  tone={delivery.spent >= campaign.spendCap ? 'bad' : 'neutral'}
                  value={delivery.spent}
                  total={campaign.spendCap}
                />
              )}
            </span>
          )}
        </Fact>
      </dl>

      {delivery.ads.length > 0 && (
        <div data-role="meta-ad-delivery-ads" className="flex flex-wrap gap-2">
          {countStatuses(delivery.ads.map((one) => one.status)).map(([status, n]) => (
            <Badge key={status} tone={DELIVERY_STATUS_TONE[status]}>
              {formatCount(n)} {n === 1 ? 'ad' : 'ads'} {DELIVERY_STATUS_LABEL[status]}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/** Ad counts by status, most first, only statuses that have any. */
function countStatuses(statuses: readonly MetaDeliveryStatus[]): [MetaDeliveryStatus, number][] {
  const counts = new Map<MetaDeliveryStatus, number>();
  for (const status of statuses) counts.set(status, (counts.get(status) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

/* -------------------------------------------------------------------------- */
/* The history                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Every change Wend sent, newest first: what, the money old and new, who, when,
 * and — for one that stopped — why, as a label. The sentence the person read at
 * the time is not stored (see `metaAdControlEventSchema`), so none is drawn.
 */
export function MetaAdHistory({ history, timezone }: { history: MetaAdControlHistory; timezone: string | null }) {
  const money = (minor: number | null, action: 'budget' | 'spend_cap'): ReactNode => {
    if (minor === null) return action === 'spend_cap' ? 'None' : '—';
    return formatMetaMoney(minor, history.currency) ?? history.currency;
  };

  /* One grid for every row: the action in a column of its own, so the money,
     the label, who and when start at the same edge on every line. */
  return (
    <div className="flex flex-col gap-3">
      <ol data-role="meta-ad-history" className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-3">
        {history.events.map((event, index) => (
          <li
            key={`${event.at}-${String(index)}`}
            data-role="meta-ad-history-event"
            data-outcome={event.outcome}
            className="col-span-2 grid grid-cols-subgrid items-start"
          >
            <span>
              <Badge tone="neutral">{ACTION_LABEL[event.action]}</Badge>
            </span>
            <span className="flex flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2 text-text">
                {(event.action === 'budget' || event.action === 'spend_cap') && (
                  <Mono>
                    {money(event.before, event.action)} → {money(event.after, event.action)}
                  </Mono>
                )}
                {event.stop !== null && (
                  <Badge tone={stoppedTone(event.action, event.stop)} data-stop={event.stop}>
                    {STOP_LABEL[event.stop]}
                  </Badge>
                )}
              </span>
              <span className="flex flex-wrap gap-x-3 font-mono text-[0.72rem] tabular-nums text-muted">
                <span>{event.actor ?? '—'}</span>
                <span>{formatInstantIn(event.at, timezone)}</span>
              </span>
            </span>
          </li>
        ))}
      </ol>
      {history.truncated && <StateChip state="under_floor" label="Older changes not shown" />}
    </div>
  );
}
